// src/utils/internalHref.js
//
// Decides whether a link destination stays inside this app, and what path it
// lands on. Kept free of any React or router import so it can be unit tested:
// react-router-dom v7 is ESM-only and this project's Jest cannot resolve it,
// which is also why src/App.test.js has never run.
//
// Resolution matters. Content authors write relative destinations
// (`alfa-romeo`, `audi/`) as often as rooted ones, and a naive "starts with a
// slash" test treats those as external, which reloads the whole application.
// Hash-only links stay with the browser so it keeps doing the scrolling, and
// `mailto:`, `tel:` and other origins fall out naturally because their
// resolved origin never matches this one.
//
// The result is also canonicalised to the single trailing slash that the
// landing, blog and guide screens redirect to. Without it, a link authored as
// `alfa-romeo` lands on a path those screens immediately replace with the
// slashed version, so the destination renders twice: once against the previous
// page's content, then again after the redirect. Invisible while every link
// was a full page load; very visible once they were not.

import { canonicalizeTrailingSlash } from './canonicalPath';

// A last segment containing a dot names a file (`/sitemap.xml`), which has to
// keep its exact path.
function canonicalisePath(pathname) {
  const lastSegment = pathname.split('/').filter(Boolean).pop() || '';
  return lastSegment.includes('.')
    ? pathname
    : canonicalizeTrailingSlash(pathname);
}

export function resolveInternalHref(href) {
  if (typeof href !== 'string') return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith('#')) return null;

  if (typeof window === 'undefined' || !window.location) {
    return trimmed.startsWith('/') && !trimmed.startsWith('//')
      ? canonicalisePath(trimmed)
      : null;
  }
  try {
    const url = new URL(trimmed, window.location.href);
    if (url.origin !== window.location.origin) return null;
    return `${canonicalisePath(url.pathname)}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function isInternalHref(href) {
  return resolveInternalHref(href) !== null;
}
